import {
  BrandDeepDiveChartRespDataLookup,
  BrandDependencyCategory,
  BrandDependencyChartData,
  BrandDependencyChartResp,
  BrandDependencyChartSubResp,
  BrandDependencyFocusArea,
  BrandDependencyKpi,
  BrandDependencyKpiDisplayName,
  BrandDependencySort,
  BrandDependencySortDisplayName,
  BrandDependencySortToKpiMapping,
  isKpiResp,
} from '@models/BrandDependency';
import { GrowthTypes, isActualGrowthType } from '@components/comparison-growth-selector/Types';
import { formatCurrencyPattern, formatKpi, Kpi, ValueType } from '@utils/Formatters';
import { FilterTitle } from '@utils/pageConfig/Types';
import { isEmpty, isPlainObject } from 'lodash';

export const mapBrandDependencyData = (chartResp: BrandDependencyChartSubResp): BrandDependencyChartData => {
  const {
    brand,
    featureValue,
    marketRevenue,
    marketRevenueShare,
    marketUnits,
    marketUnitsShare,
    retailerRevenue,
    retailerRevenueShare,
    retailerUnits,
    retailerUnitsShare,
    retailerWithinBrandRevenue,
    brandWithinRetailerRevenue,
    retailerWithinBrandUnits,
    brandWithinRetailerUnits,
  } = chartResp;

  return {
    brand,
    featureValue,
    marketRevenue,
    marketRevenueShare,
    marketUnits,
    marketUnitsShare,
    retailerRevenue,
    retailerRevenueShare,
    retailerUnits,
    retailerUnitsShare,
    retailerWithinBrand: {
      revenue: retailerWithinBrandRevenue,
      units: retailerWithinBrandUnits,
    },
    brandWithinRetailer: {
      revenue: brandWithinRetailerRevenue,
      units: brandWithinRetailerUnits,
    },
  };
};

export const getMinRangeValue = (range: string = "") => {
  const match = range.match(/\d+/g);
  if (range.includes('>') && match) return parseInt(match[0]);
  if (range.includes('<=')) return -Infinity;
  return range
}

const getSortingValue = (data: BrandDependencyChartData, key: string) => getMinRangeValue(data[key]);

export const compareValues = (
  a: string | number,
  b: string | number,
  asc: boolean
): number => {
  const compare = (x: string | number, y: string | number): number =>
    typeof x === 'number' && typeof y === 'number'
      ? x - y
      : String(x).localeCompare(String(y));
  return asc ? compare(a, b) : compare(b, a);
};

export const sortBrandDependencyData = (
  data1: BrandDependencyChartData,
  data2: BrandDependencyChartData,
  sortBy: BrandDependencySort,
  category: BrandDependencyCategory,
  isDeepDiveView: boolean = false,
) => {

  const isAlphabeticalSort = [BrandDependencySort.alphabeticallyAscending, BrandDependencySort.alphabeticallyDescending].includes(sortBy);

  if (isAlphabeticalSort) {
    const key = isDeepDiveView ? 'featureValue' : 'brand';
    const valA = getSortingValue(data1, key);
    const valB = getSortingValue(data2, key);
    const isAscending = sortBy === BrandDependencySort.alphabeticallyAscending;

    return compareValues(valA, valB, isAscending);
  }

  return data2[BrandDependencySortToKpiMapping[sortBy]][category].sharePercentage -
  data1[BrandDependencySortToKpiMapping[sortBy]][category].sharePercentage;
}

export const getBrandDependencyKpiCategory = (kpi: BrandDependencyKpi): BrandDependencyCategory =>
  [
    BrandDependencyKpi.marketRevenue,
    BrandDependencyKpi.marketRevenueShare,
    BrandDependencyKpi.retailerRevenue,
    BrandDependencyKpi.retailerRevenueShare,
  ].includes(kpi)
    ? BrandDependencyCategory.revenue
    : BrandDependencyCategory.units;

export const isBrandDependencyKpiRevenue = (kpi: BrandDependencyKpi): boolean =>
  BrandDependencyCategory.revenue === getBrandDependencyKpiCategory(kpi);

const getBrandDependencyKpiFormatters = (
  kpi: BrandDependencyKpi,
  selectedGrowthType: GrowthTypes,
  currencySymbol: string,
) => {
  switch (kpi) {
    case BrandDependencyKpi.marketRevenue:
    case BrandDependencyKpi.retailerRevenue:
      return {
        leftValueFormat: (value: number) =>
          formatCurrencyPattern(formatKpi(Kpi.marketRevenue, value || 0, { currencySymbol })),
        rightValueFormat: (value: number) =>
          formatKpi(Kpi.marketRevenue, value || 0, {
            as: isActualGrowthType(selectedGrowthType) ? ValueType.ABSOLUTE_CHANGE : ValueType.PERCENTAGE_CHANGE,
            currencySymbol,
          }),
      };
    case BrandDependencyKpi.marketUnits:
    case BrandDependencyKpi.retailerUnits:
      return {
        leftValueFormat: (value: number) => formatKpi(Kpi.marketVolume, value || 0),
        rightValueFormat: (value: number) =>
          formatKpi(Kpi.marketVolume, value || 0, {
            as: isActualGrowthType(selectedGrowthType) ? ValueType.ABSOLUTE_CHANGE : ValueType.PERCENTAGE_CHANGE,
          }),
      };
    case BrandDependencyKpi.marketRevenueShare:
    case BrandDependencyKpi.retailerRevenueShare:
      return {
        leftValueFormat: (value: number) => formatKpi(Kpi.marketShareRevenue, value || 0),
        rightValueFormat: (value: number) =>
          formatKpi(Kpi.marketShareRevenue, value || 0, { as: ValueType.ABSOLUTE_CHANGE }),
      };
    case BrandDependencyKpi.marketUnitsShare:
    case BrandDependencyKpi.retailerUnitsShare:
      return {
        leftValueFormat: (value: number) => formatKpi(Kpi.marketShareVolume, value || 0),
        rightValueFormat: (value: number) =>
          formatKpi(Kpi.marketShareVolume, value || 0, { as: ValueType.ABSOLUTE_CHANGE }),
      };
  }
};

export const mapY2Data = (
  data: BrandDependencyChartData,
  kpi: BrandDependencyKpi,
  selectedGrowthType: GrowthTypes,
  currencySymbol: string,
) => {
  const formatters = getBrandDependencyKpiFormatters(kpi, selectedGrowthType, currencySymbol);

  const kpiData = data[kpi];
  if (isKpiResp(kpiData)) {
    return {
      leftValue: kpiData.value,
      rightValue: isActualGrowthType(selectedGrowthType) ? kpiData.actual : kpiData.percentage,
      ...formatters,
    };
  } else {
    return {
      leftValue: kpiData.sharePercentage,
      rightValue: kpiData.growthPercentage,
      ...formatters,
    };
  }
};

export interface BrandDependencyKpiOption {
  label: string;
  value: BrandDependencyKpi;
}

export interface BrandDependencySortOption {
  label: string;
  value: BrandDependencySort;
}

export const kpiToOption = (kpi: BrandDependencyKpi): BrandDependencyKpiOption => ({
  value: kpi,
  label: BrandDependencyKpiDisplayName[kpi],
});

export const sortByToOption = (sortBy: BrandDependencySort): BrandDependencySortOption => ({
  value: sortBy,
  label: BrandDependencySortDisplayName[sortBy],
});

const getDataByKpi = (focusArea: BrandDependencyFocusArea) => (category: BrandDependencyCategory) => {
  const refObj = {
    [BrandDependencyFocusArea.market]: {
      [BrandDependencyCategory.revenue]: BrandDependencyKpi.marketRevenue,
      [BrandDependencyCategory.units]: BrandDependencyKpi.marketUnits,
    },
    [BrandDependencyFocusArea.retailer]: {
      [BrandDependencyCategory.revenue]: BrandDependencyKpi.retailerRevenue,
      [BrandDependencyCategory.units]: BrandDependencyKpi.retailerUnits,
    },
  } as const;

  return refObj[focusArea][category];
};

export const getMarketDataByKpi = (category: BrandDependencyCategory) =>
  getDataByKpi(BrandDependencyFocusArea.market)(category);

export const getRetailerDataByKpi = (category: BrandDependencyCategory) =>
  getDataByKpi(BrandDependencyFocusArea.retailer)(category);

export const createDataLookup = (
  dataLookupArr?: BrandDeepDiveChartRespDataLookup[],
): Map<string, BrandDeepDiveChartRespDataLookup> => {
  if (!dataLookupArr) {
    return new Map<string, BrandDeepDiveChartRespDataLookup>();
  }
  return dataLookupArr.reduce((acc, elem) => {
    acc.set(elem.brand, elem);
    return acc;
  }, new Map<string, BrandDeepDiveChartRespDataLookup>());
};

export const getChartSubtitles = (bySegmentLabel: string, selectLabel: string, withinLabel: string): string => {
  return bySegmentLabel.includes(FilterTitle.SELECT) ? selectLabel : withinLabel.concat(bySegmentLabel);
};

export const getBrandColor = (brand: string, brandIndexMap: Map<string, number>, colorMap: string[]): string => {
  return brandIndexMap.get(brand) ? colorMap[brandIndexMap.get(brand)!] : colorMap[0];
};

export const isBrandList = (brandList?: string[]): boolean => {
  return !isEmpty(brandList);
};

export const getBrandLabel = (data: BrandDependencyChartData, viewingBrand: string): string => {
  return data.brand ?? viewingBrand;
};

export const getBrandsFromChartData = (chartData?: BrandDependencyChartResp): string[] => {
  let brandList: any[] = [];

  const chartDataArr = [
    chartData?.marketRevenue,
    chartData?.marketRevenueShare,
    chartData?.marketUnits,
    chartData?.marketUnitsShare,
    chartData?.retailerRevenue,
    chartData?.retailerRevenueShare,
    chartData?.retailerUnits,
    chartData?.retailerUnitsShare,
  ];

  chartDataArr.forEach((data) => {
    if (data) {
      brandList.push(...data.map((element) => element?.brand));
    }
  });

  return brandList;
};

export const getSortedBrandColorCodes = (
  isDeepDiveView: boolean,
  sortedChartData: BrandDependencyChartData[],
  brandsColorMap: { [key: string]: string },
  viewingBrand: string,
) => {
  const chartColorMap = isDeepDiveView
    ? sortedChartData.map(() => String(brandsColorMap[viewingBrand.toUpperCase()]))
    : sortedChartData.map((chartData: BrandDependencyChartData) =>
        String(brandsColorMap[String(chartData.brand).toUpperCase()]),
      );

  return Object.values(chartColorMap);
};

export const ensureButterflyDataPercentages = (data: unknown): unknown => {
  const normalize = (entity: unknown): unknown => {
    if (Array.isArray(entity)) return entity.map(normalize);
    if (!isPlainObject(entity)) return entity;
    const normalized = Object.fromEntries(Object.entries(entity).map(([k, v]) => [k, normalize(v)]));
    return Object.hasOwn(normalized, 'value') && Object.hasOwn(normalized, 'actual') && normalized.percentage == null
      ? { ...normalized, percentage: 0 }
      : normalized;
  };
  return Array.isArray(data) ? data.map(normalize) : normalize(data);
};
